import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import {
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE,
  ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_RANKINGS,
  ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_RANKINGS_CONTENDER,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { getSafePreviousLocation } from '@sorare/core/src/lib/navigation/getSafePreviousLocation';

import { DivisionSelector } from 'components/leaderboard/DivisionSelector';
import { PaginatedRankings } from 'components/leaderboard/PaginatedRankings';
import { WithFixtureHeaderLayout } from 'layouts/WithFixtureHeaderLayout';

import type {
  RankingsPage_so5Leaderboard,
  RankingsPage_so5LeaderboardContender,
} from './__generated__/index.graphql';

type Props = {
  so5Leaderboard: RankingsPage_so5Leaderboard;
  so5LeaderboardContender?: Nullable<RankingsPage_so5LeaderboardContender>;
};

export const RankingsPage = ({
  so5Leaderboard,
  so5LeaderboardContender,
}: Props) => {
  const navigate = useNavigate();
  const { generateSportContextPath, sport } = useSportContext();
  const { so5Fixture } = so5Leaderboard || {};

  const safePreviousLocation = getSafePreviousLocation(
    generateSportContextPath(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE, {
      params: {
        eventType: so5Fixture.type.toLowerCase(),
        fixture: so5Fixture?.slug || '',
      },
    })
  );

  if (!so5Leaderboard) return null;

  const hasDivisionLeaderboards =
    so5Leaderboard.so5LeagueTrack.so5Leaderboards.length > 1;

  return (
    <WithFixtureHeaderLayout
      so5Fixture={so5Leaderboard.so5Fixture}
      title={
        <FormattedMessage
          {...fantasy.nameLeaderboard}
          values={{
            displayName:
              sport === Sport.FOOTBALL
                ? so5Leaderboard.so5LeaderboardGroup.displayName
                : so5Leaderboard.displayName,
          }}
        />
      }
      iconUrl={
        sport === Sport.FOOTBALL
          ? so5Leaderboard.so5LeaderboardGroup.iconUrl
          : so5Leaderboard.iconUrl
      }
      backTo={safePreviousLocation}
      action={
        !!hasDivisionLeaderboards && (
          <DivisionSelector
            selectedSo5Leaderboard={so5Leaderboard}
            so5Leaderboards={so5Leaderboard.so5LeagueTrack.so5Leaderboards}
            onChange={so5LeaderboardSlug => {
              navigate(
                generateSportContextPath(
                  so5LeaderboardContender
                    ? ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_RANKINGS_CONTENDER
                    : ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_RANKINGS,
                  {
                    params: {
                      eventType: so5Leaderboard.so5Fixture.type.toLowerCase(),
                      leaderboard: so5LeaderboardSlug,
                      contender: so5LeaderboardContender?.slug ?? '',
                    },
                  }
                ),
                { replace: true }
              );
            }}
          />
        )
      }
    >
      <PaginatedRankings
        key={so5Leaderboard.slug}
        slug={so5Leaderboard.slug}
        so5Ranking={
          so5LeaderboardContender?.so5Leaderboard.slug === so5Leaderboard.slug
            ? so5LeaderboardContender?.so5Lineup?.so5Rankings[0]
            : undefined
        }
      />
    </WithFixtureHeaderLayout>
  );
};

RankingsPage.fragments = {
  so5Leaderboard: gql`
    fragment RankingsPage_so5Leaderboard on So5Leaderboard {
      slug
      displayName
      iconUrl
      so5Fixture {
        slug
        type
        ...WithFixtureHeaderLayout_so5Fixture
      }
      so5LeaderboardGroup {
        slug
        displayName
        iconUrl
      }
      so5LeagueTrack {
        slug
        so5Leaderboards {
          slug
          ...DivisionSelector_so5Leaderboard
        }
      }
      ...DivisionSelector_so5Leaderboard
    }
    ${DivisionSelector.fragments.so5Leaderboard}
    ${WithFixtureHeaderLayout.fragments.so5Fixture}
  ` as TypedDocumentNode<RankingsPage_so5Leaderboard>,
  so5LeaderboardContender: gql`
    fragment RankingsPage_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
        so5Rankings {
          id
          ...PaginatedRankings_so5Ranking
        }
      }
      so5Leaderboard {
        slug
      }
    }
    ${PaginatedRankings.fragments.so5Ranking}
  ` as TypedDocumentNode<RankingsPage_so5LeaderboardContender>,
};
